import { z } from 'zod';
import { AutorDaMensagemSchema } from './autoria';
import { LIMITES } from './limites';
import { SeqSchema } from './envelope';
import { RelatorioDeCapacidadesSchema } from './claude-local';
import { AgenteOpencodeSchema } from './opencode';
import { ModeloDisponivelSchema } from './sdk';
import { MotorDeCotaSchema } from './motor';

export const PapelMaquinaSchema = z.enum(['pc', 'vps']);
export type PapelMaquina = z.infer<typeof PapelMaquinaSchema>;

export const RaizSchema = z.object({
  caminho: z.string().min(1),
  modo: z.enum(['rw', 'ro']),
});
export type Raiz = z.infer<typeof RaizSchema>;

/** Apresentação do daemon ao conectar. Serve para reconciliar estado após reinício. */
export const AgenteOlaSchema = z.object({
  hostname: z.string().min(1),
  papel: PapelMaquinaSchema,
  versaoAgente: z.string().min(1),
  versaoClaude: z.string().nullable(),
  /*
    A irma do `versaoClaude` acima — e, ao contrario do `codex` do pulso, esta
    TEM `.default(null)`. Nao e inconsistencia: e a consequencia de os dois
    serem parseados de jeitos diferentes, conferido no gateway, nao deduzido.

    O `ola` e parseado INTEIRO (`AgenteOlaSchema.safeParse`, agente.gateway.ts:781).
    Campo obrigatorio que falte nao degrada um retrato: recusa o handshake, e a
    maquina nunca fica ONLINE. O padrao aqui vale so quando a chave falta de
    verdade, e nesse caso `null` ("nao sei a versao") e exatamente a verdade.

    O pulso e parseado com `.partial()` (agente.gateway.ts:854), e ali zod
    aplicaria o default MESMO COM A CHAVE AUSENTE — apagando a distincao entre
    "nao mandou" e "mandou dizendo que nao ha". Por isso la nao tem default.
  */
  versaoCodex: z.string().nullable().default(null),
  /**
   * A irmã do `versaoCodex` para o terceiro motor (F16), com `.default(null)`
   * pela MESMA razão: o `ola` é parseado inteiro, e daemon anterior à 0.7.0 não
   * manda a chave — o que é exatamente "não sei a versão".
   */
  versaoOpencode: z.string().nullable().default(null),
  so: z.string().min(1),
  raizes: z.array(RaizSchema),
  /** Só leitura, e só para ADMIN: o que dá para espiar fora das raízes. */
  raizesEspiada: z.array(RaizSchema).default([]),
  capacidadeSdk: z.number().int().min(0).max(16),
  authMode: z.enum(['oauth', 'setup_token', 'api_key']),
  sessoesSdkAtivas: z.array(z.string()).default([]),
  trabalhosEmCurso: z.array(z.string()).default([]),
  /** O relatório da sonda (ADR-0010); ausente em daemon antigo. */
  capacidades: RelatorioDeCapacidadesSchema.optional(),
});
export type AgenteOla = z.infer<typeof AgenteOlaSchema>;

/**
 * O cerebro (F14) visto da maquina que o alcanca.
 *
 * **Tres estados, nao dois.** Ausente (`null` no pulso) e "esta maquina nao
 * tem cerebro configurado"; `ok: false` e "tem, e nao responde". Colapsar os
 * dois faria toda maquina da frota sem cerebro parecer cerebro quebrado, e o
 * alarme viraria ruido no primeiro dia.
 *
 * Campo por campo `.nullable()` porque o bridge pode responder sem saber: um
 * indice recem-criado nunca foi ingerido, e `null` ali e "nunca rodou". Data
 * inventada (agora, epoch) seria o painel afirmando o que nao sabe.
 *
 * `medidoEm` existe para a tela nao confiar em numero velho: a leitura do
 * `/status` tem cadencia propria, mais lenta que o pulso, e um daemon com o
 * laco travado continuaria mandando o ultimo retrato como se fosse de agora.
 */
export const AgenteCerebroSchema = z.object({
  ok: z.boolean(),
  medidoEm: z.iso.datetime(),
  versao: z.string().max(40).nullable().default(null),
  modelo: z.string().max(120).nullable().default(null),
  pedacos: z.number().int().nonnegative().nullable().default(null),
  /** Ultimo ciclo de ingestao que deu CERTO. */
  ultimaIngestaoEm: z.iso.datetime().nullable().default(null),
  /** Ultima tentativa, tenha dado certo ou nao. */
  ultimaTentativaEm: z.iso.datetime().nullable().default(null),
  ingestaoOk: z.boolean().nullable().default(null),
  rssMb: z.number().nonnegative().nullable().default(null),
  /** Por que nao respondeu. Nunca carrega token: e texto para humano ler. */
  erro: z.string().max(200).nullable().default(null),
});
export type AgenteCerebro = z.infer<typeof AgenteCerebroSchema>;

/**
 * O Codex visto da maquina que o hospeda — o retrato que o VigiaDoCodex tira
 * sozinho, sem esperar sessao nenhuma (D24).
 *
 * **Tres estados, nao dois**, pela mesma razao do `AgenteCerebroSchema` acima:
 * ausente (`null` no pulso) e "esta maquina nao tem Codex configurado";
 * `ok: false` e "tem, e nao responde". Colapsar os dois faria toda maquina da
 * frota sem Codex — que hoje sao todas menos uma — parecer Codex quebrado, e o
 * alarme `codex_fora` nasceria como ruido.
 *
 * Campo por campo `.nullable()` porque o app-server responde sem saber de tudo:
 * um perfil que nunca abriu thread nao tem `rssMb`, e `null` ali e "nunca
 * mediu". Numero inventado seria o painel afirmando o que nao apurou.
 *
 * `medidoEm` existe para a tela nao confiar em retrato velho: o laco do vigia
 * tem cadencia propria, mais lenta que o pulso, e um daemon com o laco travado
 * continuaria repetindo a ultima medicao como se fosse de agora.
 */
export const AgenteCodexSchema = z.object({
  ok: z.boolean(),
  medidoEm: z.iso.datetime(),
  /** De onde sai depende de M0-5; `null` enquanto nao se sabe, nunca chutado. */
  versao: z.string().max(40).nullable().default(null),
  /** Quantos app-server de pe. E por PERFIL, nunca por sessao: teto de 2. */
  processos: z.number().int().nonnegative().max(8).nullable().default(null),
  /** Threads vivas somadas entre os perfis. */
  threads: z.number().int().nonnegative().nullable().default(null),
  rssMb: z.number().nonnegative().nullable().default(null),
  /*
    O par de login por perfil, espelhando `loginMestreOk`/`loginTrabalhoOk` que
    o pulso ja tem para o Claude. `null` e "nao apurado", que nao e o mesmo que
    `false` ("apurado, e esta deslogado") — e a diferenca importa porque quem
    resolve login e o dono, com as proprias maos, e um `false` errado o mandaria
    rodar `codex login` sem precisar.
  */
  loginMestreOk: z.boolean().nullable().default(null),
  loginTrabalhoOk: z.boolean().nullable().default(null),
  /** Por que nao respondeu. Nunca carrega token: e texto para humano ler. */
  erro: z.string().max(200).nullable().default(null),
  /**
   * O catalogo de modelos daquela maquina, lido do `model/list`.
   *
   * Ele viaja AQUI, e nao so nas capacidades da sessao, porque quem precisa
   * dele primeiro e a tela de CRIAR: ali ainda nao existe sessao nenhuma, e
   * `sdk.capacidades` so chega depois. Sem lista, a tela teria de inventar um
   * nome de modelo — que e exatamente o que a regra da casa proibe — ou pedir
   * texto livre, que da no mesmo com mais passos.
   *
   * Acrescimo OPCIONAL com `.default([])`: a regra da casa diz que campo novo
   * com default nao sobe versao, e um daemon que ainda nao o manda continua
   * valendo. Lista vazia e informacao honesta: a tela desabilita criar e diz
   * por que, em vez de oferecer um modelo que ninguem confirmou existir.
   */
  modelos: z.array(ModeloDisponivelSchema).max(60).default([]),
});
export type AgenteCodex = z.infer<typeof AgenteCodexSchema>;

/**
 * O perfil de um motor dentro da máquina. No Codex são dois `CODEX_HOME`
 * (mestre e trabalho), e cada um pode estar logado numa conta; no Claude a
 * máquina tem um diretório de configuração só, e o perfil vai `null`.
 */
export const PerfilDaContaSchema = z.enum(['mestre', 'trabalho']);
export type PerfilDaConta = z.infer<typeof PerfilDaContaSchema>;

/**
 * A conta de assinatura em que um motor da máquina está logado AGORA, como o
 * próprio CLI a guarda (os arquivos de conta do Claude, `account/read` do Codex).
 *
 * Existe porque a frota deixou de ter uma conta por motor (decisão do dono,
 * 2026-09-24): a VPS e o PC podem estar em contas diferentes, e a cota de uma
 * não diz nada da outra. O e-mail é a identidade — é o que o dono reconhece e
 * o que os dois CLIs devolvem. Conta sem e-mail (chave de API, Bedrock) não
 * tem cota de assinatura, e por isso não vira linha.
 */
export const ContaInformadaSchema = z.object({
  // Só os motores de COTA de assinatura: o OpenCode paga por token numa chave da
  // FROTA (D14), e não há conta por máquina a informar.
  motor: MotorDeCotaSchema,
  perfil: PerfilDaContaSchema.nullable(),
  email: z.string().min(3).max(320),
  /** O plano como o serviço o chama (`max`, `prolite`…). Nunca traduzido aqui. */
  plano: z.string().max(100).nullable(),
});
export type ContaInformada = z.infer<typeof ContaInformadaSchema>;

export const AgentePulsoSchema = z.object({
  memoriaLivreMb: z.number().int().nonnegative(),
  sessoesSdkAtivas: z.number().int().nonnegative(),
  cargaCpu: z.number().nonnegative().nullable(),
  loginMestreOk: z.boolean().nullable(),
  loginTrabalhoOk: z.boolean().nullable(),
  loginExpiraEm: z.iso.datetime().nullable(),
  /*
    Campo NOVO entra opcional, nunca como evento novo — a regra do protocolo.
    Daemon antigo nao manda `cerebro`, e o pulso dele continua valendo; API
    antiga recebe e ignora. Nada de subir `v`.

    **De proposito SEM `.default()`.** O gateway parseia com `.partial()`, e
    zod aplica o `.default()` de um campo mesmo quando a CHAVE esta ausente do
    objeto de entrada — o que apagaria a distincao entre "o daemon nao manda
    este campo" (chave ausente: preservar o ultimo retrato) e "o daemon manda
    e diz que nao ha cerebro" (`null` explicito: LIMPAR a coluna). Com
    `.default()` os dois casos chegavam identicos no `agente.gateway.ts`, e um
    daemon antigo qualquer apagaria silenciosamente o retrato de toda maquina
    a cada pulso — achado plantando o caso no teste, nao por inspecao.
  */
  cerebro: AgenteCerebroSchema.nullable(),
  /*
    O Codex visto desta maquina, pela MESMA regra do `cerebro` logo acima — e
    pelo mesmo motivo, que ja custou um achado: o gateway parseia este pulso com
    `AgentePulsoSchema.partial()` (conferido em agente.gateway.ts:854, nao
    deduzido), e zod aplica o `.default()` de um campo MESMO COM A CHAVE
    AUSENTE. Com default, "o daemon nao manda" e "o daemon diz que nao ha"
    chegariam identicos, e todo daemon sem Codex apagaria o retrato a cada pulso.

    Repare no contraste com o `versaoCodex` do `AgenteOlaSchema`, que TEM
    default: o `ola` e parseado inteiro (`safeParse`, agente.gateway.ts:781),
    entao ali o padrao vale so quando a chave falta de verdade.
  */
  codex: AgenteCodexSchema.nullable(),
  /*
    O OpenCode (F16), pela MESMA regra dos dois de cima e pelo mesmo motivo:
    `.nullable()` e SEM `.default()`. O gateway parseia o pulso com
    `.partial()`, e zod aplicaria o default mesmo com a chave ausente — um
    daemon anterior à 0.7.0, que nem sabe o que é OpenCode, apagaria o retrato
    de toda máquina a cada pulso. Ausente preserva; `null` limpa.
  */
  opencode: AgenteOpencodeSchema.nullable(),
  /*
    As contas em que os motores desta máquina estão logados, pela MESMA regra
    do `cerebro` e do `codex` acima — sem `.default()`, porque o pulso é
    parseado com `.partial()`: ausente é daemon anterior à 0.7.1 (a coluna fica
    como estava), lista é o retrato de agora (vazia: nenhuma conta
    identificável). A API carimba cada evento gravado com a conta vigente.
  */
  contas: z.array(ContaInformadaSchema).max(6).nullable().optional(),
  /*
    A versão do Claude Code AGORA (F5 da revisão de out/2026). Até aqui ela só
    ia no `ola`, lida no boot — e depois que o daemon atualiza o CLI sozinho o
    painel mostraria a versão velha até reiniciar. Mesma regra dos de cima:
    SEM `.default()` (o pulso é parseado com `.partial()`). Ausente é daemon
    anterior; `null` é "não consegui ler" — e a coluna fica, porque apagar uma
    versão conhecida por uma leitura que falhou seria trocar informação por nada.
  */
  versaoClaude: z.string().min(1).max(40).nullable().optional(),
});
export type AgentePulso = z.infer<typeof AgentePulsoSchema>;

/**
 * O que aconteceu na atualização do Claude Code que o daemon fez sozinho (F5,
 * D27): `agente.atualizacao_claude`, daemon → API, com ack.
 *
 * - `atualizado`: a versão subiu e o teste de compatibilidade passou.
 * - `sem_novidade`: o CLI já estava na última.
 * - `falhou`: o `claude update` não trocou nada (rede, servidor); nada a voltar.
 * - `voltou`: a versão nova reprovou no teste e a anterior foi reinstalada.
 * - `quebrado`: reprovou e a volta TAMBÉM falhou — a máquina está numa versão
 *   que o teste reprovou, e é o caso que pede gente.
 *
 * `motivo` é o texto do CLI (ou do teste), **palavra por palavra** e cortado no
 * teto: reescrever funcionaria hoje e mentiria no dia em que a razão mudasse (a
 * lição do `erro-do-motor`). `strictObject`: a API recusa o que não conhece.
 */
export const RESULTADOS_DA_ATUALIZACAO_DO_CLAUDE = [
  'atualizado',
  'sem_novidade',
  'falhou',
  'voltou',
  'quebrado',
] as const;
export const AtualizacaoDoClaudeSchema = z.strictObject({
  em: z.iso.datetime(),
  de: z.string().min(1).max(40).nullable(),
  para: z.string().min(1).max(40).nullable(),
  resultado: z.enum(RESULTADOS_DA_ATUALIZACAO_DO_CLAUDE),
  motivo: z.string().max(1_500).nullable(),
});
export type AtualizacaoDoClaude = z.infer<typeof AtualizacaoDoClaudeSchema>;

/** Sessão viva vista pelo daemon (claude agents --json + sessions/*.json). */
export const SessaoVivaSchema = z.object({
  sessionId: z.string().min(1),
  cwd: z.string().min(1),
  pid: z.number().int().positive().nullable(),
  nome: z.string().nullable(),
  entrypoint: z.string().nullable(),
  kind: z.string().nullable(),
  bridgeSessionId: z.string().nullable(),
  iniciadaEm: z.iso.datetime().nullable(),
});
export type SessaoViva = z.infer<typeof SessaoVivaSchema>;

/**
 * Sessao antiga encontrada no disco. Entra encerrada e nao participa da
 * reconciliacao das vivas: importar historico nao pode fazer o painel achar
 * que uma conversa de tres meses atras voltou a acontecer.
 */
export const SessaoHistoricaSchema = z.object({
  sessionId: z.string().min(1),
  cwd: z.string().min(1),
  nome: z.string().nullable(),
  tituloIa: z.string().nullable(),
  versaoClaude: z.string().nullable(),
  entrypoint: z.string().nullable(),
  iniciadaEm: z.iso.datetime().nullable(),
  ultimaAtividadeEm: z.iso.datetime().nullable(),
});
export type SessaoHistorica = z.infer<typeof SessaoHistoricaSchema>;

export const LoteHistoricoSchema = z.object({
  sessoes: z.array(SessaoHistoricaSchema).min(1).max(100),
});
export type LoteHistorico = z.infer<typeof LoteHistoricoSchema>;

export const OrigemEventoSchema = z.enum(['jsonl', 'gancho', 'sdk']);
export type OrigemEvento = z.infer<typeof OrigemEventoSchema>;

/** Evento bruto de sessão que o daemon manda; a API é quem atribui seq. */
export const EventoSessaoEntradaSchema = z.object({
  origem: OrigemEventoSchema,
  tipo: z.string().min(1),
  /**
   * A chave de dedupe do turno — `uuid` no Claude, `item.id` com sufixo por
   * sub-evento no Codex. Sem ela nao ha idempotencia: no Postgres `NULL` nunca
   * e igual a `NULL`, entao o unique do banco nao protege linha sem uuid.
   */
  uuidTurno: z.string().optional(),
  /** A versao do CLI que produziu o evento; qual CLI, diz o motor da sessao. */
  versaoMotor: z.string().optional(),
  /**
   * Quem mandou esta mensagem, quando o daemon consegue afirmar (`autoria.ts`).
   *
   * **Só existe em evento de mensagem do usuário, e só quando há vínculo
   * EXATO**: o daemon carimba pela marca que ele mesmo pôs no envio e que o
   * motor devolveu — nunca casando texto. Ausente significa "não se sabe", e
   * não "foi o dono": mensagem antiga, mensagem digitada no editor e evento
   * que o daemon reenviou depois de perder o carimbo caem todos aqui, e o
   * painel não inventa nome para nenhum deles.
   *
   * Campo NOVO e opcional: daemon velho continua valendo e `VERSAO_PROTOCOLO`
   * não sobe (`.claude/rules/protocolo.md`) — como `uuidTurno` e `versaoMotor`,
   * os dois vizinhos, já fazem.
   */
  autor: AutorDaMensagemSchema.optional(),
  ts: z.iso.datetime().optional(),
  carga: z.record(z.string(), z.unknown()),
  bruto: z.string().max(LIMITES.brutoBytes).optional(),
  truncado: z.boolean().default(false),
  arquivo: z.string().optional(),
  offset: z.number().int().nonnegative().optional(),
});
export type EventoSessaoEntrada = z.infer<typeof EventoSessaoEntradaSchema>;

/**
 * O lote de eventos que o daemon manda.
 *
 * O id vem do MOTOR, e por isso nao se chama mais `sessionIdClaude`: no Claude
 * e o `sessionId` do CLI, no Codex e o `threadId` da thread.
 *
 * Lote no formato antigo nao e aceito nem gravado com campo nulo: ele e
 * recusado pelo parse e o ack manda para a QUARENTENA com `PROTOCOLO`, que
 * avanca o offset — aquele lote nunca vai colar, e reenviar para sempre seria
 * travar a fila num evento que ninguem mais entende.
 */
export const LoteEventosSchema = z.object({
  idNoMotor: z.string().min(1),
  eventos: z.array(EventoSessaoEntradaSchema).min(1).max(LIMITES.loteEventos),
});
export type LoteEventos = z.infer<typeof LoteEventosSchema>;

/**
 * Pedido de leitura do transcript direto na maquina.
 *
 * O historico ja existe em disco onde a sessao rodou. Copiar tudo para o banco
 * de um servidor compartilhado seria pagar duas vezes pelo mesmo dado - e
 * transcript de mais de um giga existe. Entao o banco guarda o que esta vivo,
 * e o resto e lido na hora, de tras para frente, so quando alguem abre.
 */
export const LerTranscriptSchema = z.object({
  sessionIdClaude: z.string().min(1),
  /** Fim do trecho pedido; ausente significa o fim do arquivo. */
  ateOffset: z.number().int().nonnegative().optional(),
  bytes: z
    .number()
    .int()
    .min(4 * 1024)
    .max(2 * 1024 * 1024)
    .default(256 * 1024),
});
export type LerTranscript = z.infer<typeof LerTranscriptSchema>;

export const TrechoTranscriptSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    eventos: z.array(EventoSessaoEntradaSchema),
    /** Onde o trecho comeca: use como `ateOffset` para pedir o anterior. */
    inicioOffset: z.number().int().nonnegative(),
    tamanhoArquivo: z.number().int().nonnegative(),
    temMais: z.boolean(),
  }),
  z.object({
    ok: z.literal(false),
    erro: z.enum(['SEM_ARQUIVO', 'FORA_DA_RAIZ', 'FALHA']),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type TrechoTranscript = z.infer<typeof TrechoTranscriptSchema>;

export const EventoReplayavelSchema = EventoSessaoEntradaSchema.extend({
  sessaoId: z.string(),
  seq: SeqSchema,
});
export type EventoReplayavel = z.infer<typeof EventoReplayavelSchema>;

/**
 * Uma janela de cota do plano, como o SDK a relata.
 *
 * `utilizacao` é sempre 0..1 aqui: o `rate_limit_event` não promete escala, e
 * normalizar já na entrada do daemon evita uma barra de progresso em 5000%.
 */
export const JanelaDeCotaSchema = z.object({
  utilizacao: z.number().min(0).max(1),
  reiniciaEm: z.iso.datetime().nullable().optional(),
});
export type JanelaDeCota = z.infer<typeof JanelaDeCotaSchema>;

/**
 * A cota do plano vista pela máquina.
 *
 * Vem do `rate_limit_event` do Agent SDK, e nunca de `/api/oauth/usage` — o
 * plano mestre veta esse endereço (não é documentado e responde 429). É
 * instantâneo, não histórico: guarda-se o último que a máquina contou.
 */
export const CotaClaudeSchema = z.strictObject({
  cincoHoras: JanelaDeCotaSchema.optional(),
  seteDias: JanelaDeCotaSchema.optional(),
  /*
    Do ADR-0021 para baixo, tudo opcional e sem `.default()`: o daemon anterior
    à 0.6.0 não manda nenhum, e ausente é "a máquina não disse", nunca zero.
  */
  /** As janelas semanais por família, que o `rate_limit_event` já trazia e o daemon descartava. */
  seteDiasOpus: JanelaDeCotaSchema.optional(),
  seteDiasSonnet: JanelaDeCotaSchema.optional(),
  /**
   * As janelas por modelo (`model_scoped`: Fable etc.), que só a chamada
   * experimental do SDK conta. O rótulo é o do serviço, nunca traduzido aqui.
   */
  porModelo: z
    .array(
      z.object({
        rotulo: z.string().min(1).max(100),
        utilizacao: z.number().min(0).max(1),
        reiniciaEm: z.iso.datetime().nullable(),
      }),
    )
    .max(8)
    .optional(),
  /** O uso além do plano. `utilizacao` nula: ligado, sem medida conhecida. */
  usoExtra: z
    .object({ ativo: z.boolean(), utilizacao: z.number().min(0).max(1).nullable() })
    .optional(),
  plano: z.string().max(100).nullable().optional(),
  /** De onde veio este retrato: o evento estável ou a chamada experimental (que pode sumir). */
  fonte: z.enum(['evento', 'experimental']).optional(),
  em: z.iso.datetime(),
});
export type CotaClaude = z.infer<typeof CotaClaudeSchema>;

/**
 * Uma janela de cota que diz o PRÓPRIO TAMANHO.
 *
 * O Claude tem duas janelas fixas (5 horas e 7 dias) e por isso `CotaClaudeSchema`
 * pôde nomeá-las. O Codex não: medido em M0-4, ele relata **uma** janela, de
 * `windowDurationMins: 10080` (sete dias), com `secondary: null`. Forçar esse
 * número para dentro de `cincoHoras`/`seteDias` seria rotular a janela pelo que
 * o outro motor tem — e a tela mostraria "7 dias" por acidente de coincidência,
 * até o dia em que a OpenAI mudar a janela e o rótulo virar mentira.
 *
 * Por isso a janela carrega `duracaoMin`: quem desenha lê o tamanho, não adivinha.
 */
export const JanelaDoMotorSchema = z.object({
  /** Identificador do lado de lá (`limitId` do Codex), para casar janelas entre leituras. */
  id: z.string().min(1),
  /** Rótulo que o próprio serviço deu, quando dá. Nunca inventado por nós. */
  rotulo: z.string().nullable().default(null),
  utilizacao: z.number().min(0).max(1),
  /** O tamanho da janela, em minutos. É ELE que a tela usa para escrever o nome. */
  duracaoMin: z.number().int().positive().nullable().default(null),
  reiniciaEm: z.iso.datetime().nullable().default(null),
});
export type JanelaDoMotor = z.infer<typeof JanelaDoMotorSchema>;

/**
 * A cota de um motor que não é o Claude.
 *
 * `janelas` é lista porque o número delas é do serviço, não nosso: hoje o Codex
 * manda uma, e o binding já prevê `rateLimitsByLimitId` com várias.
 */
export const CotaMotorSchema = z.strictObject({
  janelas: z.array(JanelaDoMotorSchema).max(8).default([]),
  /** O plano, como o serviço o chama (`prolite`, `plus`…). Nunca traduzido aqui. */
  plano: z.string().nullable().default(null),
  em: z.iso.datetime(),
});
export type CotaMotor = z.infer<typeof CotaMotorSchema>;

/**
 * A cota que chega da máquina, discriminada por motor.
 *
 * O `motor` é OBRIGATÓRIO aqui — ao contrário do `SdkIniciarSchema`, onde a
 * ausência vira `CLAUDE`. A diferença não é estilo: lá existe chamador legítimo
 * que não conhece o campo, aqui não. A porta de versão (`VERSAO_AGENTE_MINIMA`)
 * já barrou todo daemon anterior ao eixo, então um corpo sem `motor` não é um
 * cliente velho — é um corpo que não sabe de qual motor é o número. Tolerar
 * seria escrever a cota na coluna errada, calado, e a tela mostraria a cota do
 * Codex como se fosse a do Claude.
 */
export const AgenteCotaSchema = z.discriminatedUnion('motor', [
  z.object({ motor: z.literal('CLAUDE'), cota: CotaClaudeSchema }),
  z.object({
    motor: z.literal('CODEX'),
    cota: CotaMotorSchema,
    /**
     * De qual `CODEX_HOME` veio esta cota. Opcional: o daemon anterior à 0.7.1
     * não manda, e a cota de cada perfil caía na mesma coluna. Com as contas
     * por máquina, é ele que diz de QUAL conta é o número.
     */
    perfil: PerfilDaContaSchema.optional(),
  }),
]);
export type AgenteCota = z.infer<typeof AgenteCotaSchema>;
